import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const LoginPage = () => {
    const navigate = useNavigate();
    const { setUser, setIsAuthenticated, loginWithGoogle } = useAuth();
    const [mode, setMode] = useState('login'); // 'login' or 'register'
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ email: '', password: '', name: '' });
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            let response;
            if (mode === 'register') {
                if (!form.name.trim()) { setError('Name is required'); setLoading(false); return; }
                if (form.password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
                response = await authAPI.register({ email: form.email, password: form.password, name: form.name });
                toast.success('Account created successfully!');
            } else {
                response = await authAPI.loginEmail({ email: form.email, password: form.password });
            }

            const userData = response.data;
            setUser(userData);
            setIsAuthenticated(true);

            if (!userData.terms_accepted) {
                navigate('/terms/accept', { replace: true });
            } else {
                navigate('/welcome', { replace: true });
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(detail);
            } else if (Array.isArray(detail)) {
                setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>
                    <h1 className="font-heading text-3xl font-bold mb-2">
                        {mode === 'login' ? 'Welcome Back' : 'Create an Account'}
                    </h1>
                    <p className="text-muted-foreground">
                        {mode === 'login' ? 'Sign in to access the HAAPE Hub' : 'Join the HAAPE Hub community'}
                    </p>
                </div>

                <Card>
                    <CardContent className="pt-6">
                        {/* Google Login */}
                        <Button
                            variant="outline"
                            className="w-full gap-3 h-12 text-base"
                            onClick={loginWithGoogle}
                            data-testid="google-login-btn"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Continue with Google
                        </Button>

                        <div className="relative my-6">
                            <Separator />
                            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                                or
                            </span>
                        </div>

                        {/* Email/Password Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {mode === 'register' && (
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="name"
                                            type="text"
                                            placeholder="Your full name"
                                            className="pl-10"
                                            value={form.name}
                                            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                            data-testid="register-name"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="you@example.com"
                                        className="pl-10"
                                        value={form.email}
                                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                        required
                                        data-testid="login-email"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder={mode === 'register' ? 'Min 8 characters' : 'Your password'}
                                        className="pl-10"
                                        value={form.password}
                                        onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                        required
                                        data-testid="login-password"
                                    />
                                </div>
                            </div>

                            {error && (
                                <p className="text-sm text-destructive" data-testid="auth-error-msg">{error}</p>
                            )}

                            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="auth-submit-btn">
                                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {mode === 'login' ? 'Sign In' : 'Create Account'}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            {mode === 'login' ? (
                                <p className="text-muted-foreground">
                                    Don't have an account?{' '}
                                    <button onClick={() => { setMode('register'); setError(''); }} className="text-primary font-medium hover:underline" data-testid="switch-to-register">
                                        Sign up
                                    </button>
                                </p>
                            ) : (
                                <p className="text-muted-foreground">
                                    Already have an account?{' '}
                                    <button onClick={() => { setMode('login'); setError(''); }} className="text-primary font-medium hover:underline" data-testid="switch-to-login">
                                        Sign in
                                    </button>
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
